import Button from '@/components/Button';
import Input from '@/components/Input';
import ModalV2 from '@/components/ModalV2';
import { AiFillStar, AiOutlineStar } from '@/components/react-icons/main';
import IconV2 from '@/components/v2/IconV2';
import useUser from '@/hooks/useUser';
import { post } from '@/lib/api';
import { setModalState } from '@/redux/actions/uiActions/modalActions';
import memoizeSelector from '@/redux/lib/memoizeSelector';
import selectors from '@/redux/selectors/selectors';
import { modal } from '@/redux/selectors/uiSelectors';
import companyActions from '@/screens/workflows/redux/actions/companyActions';
import { useState } from 'react';
import { useSelector } from 'react-redux';

const StarRating = ({ rating, onRatingChange, hoveredRating, onHover }) => {
  const starValues = [1, 2, 3, 4, 5];

  const getClickValue = (e, starValue) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const isLeftHalf = x < rect.width / 2;
    return isLeftHalf ? starValue - 0.5 : starValue;
  };

  const handleStarClick = (e, starValue) => {
    const clickValue = getClickValue(e, starValue);
    onRatingChange(clickValue);
  };

  const handleStarHover = (value) => {
    onHover(value);
  };

  const handleMouseLeave = () => {
    onHover(null);
  };

  const handleMouseMove = (e, starValue) => {
    const hoverValue = getClickValue(e, starValue);
    handleStarHover(hoverValue);
  };

  const getStarState = (starValue) => {
    const displayRating = hoveredRating !== null ? hoveredRating : rating;
    const isHalf = displayRating >= starValue - 0.5 && displayRating < starValue;
    const isFull = displayRating >= starValue;
    return { isHalf, isFull };
  };

  return (
    <div className="comp-star-rating" onMouseLeave={handleMouseLeave}>
      {starValues.map((starValue) => {
        const { isHalf, isFull } = getStarState(starValue);

        return (
          <div key={starValue} className="star-wrapper">
            <div className="star-container" onClick={(e) => handleStarClick(e, starValue)} onMouseEnter={() => handleStarHover(starValue)} onMouseMove={(e) => handleMouseMove(e, starValue)}>
              <div className="star-background">
                <IconV2 icon={AiOutlineStar} />
              </div>
              {isFull && (
                <div className="star-filled">
                  <IconV2 icon={AiFillStar} />
                </div>
              )}
              {isHalf && !isFull && (
                <div className="star-half">
                  <div className="star-half-left">
                    <IconV2 icon={AiFillStar} />
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const G2ReviewModal = () => {
  const modalState = useSelector(memoizeSelector('modal:g2Review', (state) => modal(state, 'g2Review')));
  const show = modalState?.open;
  const url = modalState?.url;
  const { jwt, settings } = useUser();
  const activeCompany = useSelector(selectors.activeCompany);
  const g2Reviews = activeCompany?.settings?.g2Reviews;
  const userId = jwt?.id;

  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(null);
  const [feedback, setFeedback] = useState('');

  const handleClose = () => {
    sendWebhook();
    companyActions.set('settings.g2Reviews', { ...(g2Reviews || {}), [userId]: true });
    setModalState('g2Review', { open: false });

    // Clear form state
    setRating(0);
    setHoveredRating(null);
    setFeedback('');
  };

  const sendWebhook = (overrideRating = null) => {
    const ratingToSend = overrideRating !== null ? overrideRating : rating;
    if (ratingToSend === 0) {
      return;
    }

    const webhookData = {
      companyName: activeCompany?.name,
      rating: ratingToSend,
      feedback: feedback || null,
      userName: jwt?.name || settings?.name,
      userEmail: jwt?.email || settings?.email
    };

    // Workflow ID: 8Xc1F5IuCOsH
    post('https://webhook.mindcloud.co/v1/webhook/5e4e3e90e8ef3618f86925b552dc41ac898cd27c1add7fa6ea89c238749f6bd2a0ae6aeb039d65ce4f5a0ccf1cdef69f', {
      data: webhookData,
      action: 'POST_G2_REVIEW',
      callback: () => {}
    });
  };

  const handleRatingChange = (newRating) => {
    setRating(newRating);
    if (newRating === 5) {
      if (url) {
        sendWebhook(5);
        companyActions.set('settings.g2Reviews', { ...(g2Reviews || {}), [userId]: true });
        window.open(url, '_blank');
      }
      setModalState('g2Review', { open: false });
    }
  };

  const handleNotNow = () => {
    setModalState('g2Review', { open: false });
    setRating(0);
    setHoveredRating(null);
    setFeedback('');
  };

  const handleSend = () => {
    sendWebhook();
    handleClose();
  };

  const showFeedback = rating > 0 && rating < 5;
  const showNotNow = !showFeedback;

  return (
    <ModalV2 show={show} onHide={handleClose} centered maxWidth="400px" noHeader noFooter>
      <div className="comp-g2-review-modal">
        <div className="modal-header">
          <img src="/icons/stars.svg" alt="Stars" className="stars-icon" />
          <div className="title">Enjoying MindCloud?</div>
          <div className="subtitle">Rate your experience with us so far.</div>
        </div>
        <StarRating rating={rating} onRatingChange={handleRatingChange} hoveredRating={hoveredRating} onHover={setHoveredRating} />
        {showNotNow && (
          <Button onClick={handleNotNow} className="text medium full-width">
            Not Now
          </Button>
        )}
        {showFeedback && (
          <div className="feedback-section">
            <Input autoFocus type="textarea" placeholder="We strive for everyone to have a 5 star experience with MindCloud. What can we improve on?" value={feedback} onChange={(e) => setFeedback(e.target.value)} rows={4} />
            <Button onClick={handleSend} size="lg" className="primary full-width">
              Send
            </Button>
          </div>
        )}
      </div>
    </ModalV2>
  );
};

export default G2ReviewModal;
